import React, { useEffect } from 'react';
import Layout from '../components/layout/Layout';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import CTASection from '../components/shared/CTASection';
import { ParallaxSection, ParallaxImage, ParallaxElement, FadeInOnScroll } from '../components/shared/Parallax';
import { Check, Sparkles, Scissors, Palette, Heart, Shield } from 'lucide-react';

const TendeInternoPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const craftFeatures = [
    {
      icon: Scissors,
      title: "Sartorialità su Misura",
      description: "Ogni tenda è realizzata artigianalmente sulle esatte misure dei tuoi ambienti, per un risultato impeccabile."
    },
    {
      icon: Palette,
      title: "Tessuti Pregiati",
      description: "Selezioniamo personalmente i tessuti più raffinati: lini, sete, velluti e cotoni di alta qualità."
    },
    {
      icon: Heart,
      title: "Passione Artigianale",
      description: "Oltre 60 anni di esperienza tramandati di generazione in generazione, per un'arte che non conosce scorciatoie."
    },
    {
      icon: Sparkles,
      title: "Finiture di Pregio",
      description: "Cuciture invisibili, orlature perfette e dettagli che fanno la differenza in ogni ambiente."
    }
  ];

  const tendeTypes = [
    {
      name: "Tende a Pacchetto",
      description: "Eleganza classica che si raccoglie in morbide pieghe orizzontali, perfette per ambienti raffinati.",
      features: ["Pieghe morbide e regolari", "Tessuti naturali e pregiati", "Meccanismi silenziosi"]
    },
    {
      name: "Tende a Pannello",
      description: "Design contemporaneo con pannelli scorrevoli, ideali per grandi vetrate e spazi moderni.",
      features: ["Scorrimento fluido", "Sovrapposizioni creative", "Manutenzione minima"]
    },
    {
      name: "Tende Classiche",
      description: "L'intramontabile eleganza delle tende con mantovana e drappeggi, per interni di prestigio.",
      features: ["Drappeggi su misura", "Mantovane personalizzate", "Tessuti decorati"]
    },
    {
      name: "Tende Tecniche",
      description: "Soluzioni funzionali che uniscono controllo della luce, privacy e design minimale.",
      features: ["Oscuranti e filtranti", "Sistemi motorizzati", "Design discreto"]
    }
  ];

  const qualityPoints = [
    "Tessuti selezionati dai migliori produttori europei",
    "Lavorazione interamente artigianale",
    "Cuciture rinforzate per durabilità nel tempo",
    "Sistemi di scorrimento premium",
    "Consulenza d'arredo personalizzata",
    "Installazione professionale inclusa",
    "Garanzia di 5 anni su tutti i prodotti"
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <HeroSection
        title="Tende da Interno"
        subtitle="L'arte tessile che veste i tuoi ambienti"
        ctaText="Richiedi una consulenza d'arredo"
        ctaLink="/preventivo"
        imageUrl="https://customer-assets.emergentagent.com/job_3b930011-c4cf-4c5a-b240-bf5736a6fba8/artifacts/c65hzkep_Gemini_Generated_Image_p1x4ejp1x4ejp1x4.png"
        showScrollIndicator={true}
      />

      {/* Intro Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Artigianalità Italiana
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-tight mb-6">
                Dove la tradizione incontra l'eccellenza
              </h2>
              <p className="text-lg text-gray-600 font-light leading-relaxed">
                Da oltre 60 anni, le nostre mani esperte trasformano i tessuti più pregiati in opere 
                d'arte per i tuoi interni. Ogni tenda che realizziamo porta con sé la passione di 
                generazioni di artigiani, la cura per il dettaglio e la ricerca costante della perfezione.
              </p>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Craftsmanship Section */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="La Nostra Arte"
              title="Artigianalità senza compromessi"
              description="Ogni dettaglio è curato con la dedizione che solo le mani esperte sanno offrire."
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {craftFeatures.map((feature, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div 
                  className="p-8 lg:p-10 bg-white border border-gray-100 hover:border-sky-200 transition-all duration-500 hover:shadow-xl group flex gap-6"
                >
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-sky-500/10 flex items-center justify-center group-hover:bg-sky-500 transition-colors duration-300">
                      <feature.icon className="w-6 h-6 text-sky-500 group-hover:text-white transition-colors duration-300" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 font-light leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Types Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Le Nostre Creazioni"
              title="Soluzioni per ogni stile"
              description="Dal classico al contemporaneo, creiamo tende che si integrano perfettamente con la tua visione d'arredo."
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {tendeTypes.map((type, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div 
                  className="p-8 lg:p-10 border border-gray-100 hover:border-sky-200 transition-all duration-500 hover:shadow-xl group"
                >
                  <h3 className="text-2xl font-medium text-gray-900 mb-4 group-hover:text-sky-500 transition-colors duration-300">
                    {type.name}
                  </h3>
                  <p className="text-gray-600 font-light leading-relaxed mb-6">
                    {type.description}
                  </p>
                  <ul className="space-y-2">
                    {type.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-3 text-gray-600 font-light">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Promise Section */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll>
              <ParallaxImage
                src="https://customer-assets.emergentagent.com/job_3b930011-c4cf-4c5a-b240-bf5736a6fba8/artifacts/wxrr8dye_Gemini_Generated_Image_bd31ztbd31ztbd31.png"
                alt="Tessuti pregiati"
                speed={0.15}
                aspectRatio="4/3"
              />
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  La Nostra Promessa
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-8">
                  Qualità che si vede e si tocca
                </h2>
                <ul className="space-y-4">
                  {qualityPoints.map((point, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-6 h-6 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-sky-500" />
                      </div>
                      <span className="text-gray-700 font-light">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Il Nostro Processo"
              title="Dal sogno alla realtà"
              description="Un percorso personalizzato per creare le tende perfette per i tuoi ambienti."
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Consulenza", desc: "Visitiamo i tuoi spazi per comprendere stile e necessità." },
              { step: "02", title: "Progettazione", desc: "Proponiamo tessuti, colori e soluzioni su misura." },
              { step: "03", title: "Realizzazione", desc: "I nostri artigiani creano le tue tende con cura." },
              { step: "04", title: "Installazione", desc: "Montiamo tutto con precisione e pulizia." }
            ].map((item, index) => (
              <FadeInOnScroll key={index} delay={index * 150}>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sky-500/10 text-sky-500 text-2xl font-light mb-6">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 font-light">{item.desc}</p>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Parallax Visual Section */}
      <ParallaxSection
        imageUrl="https://customer-assets.emergentagent.com/job_3b930011-c4cf-4c5a-b240-bf5736a6fba8/artifacts/fjpckclf_Gemini_Generated_Image_epi9znepi9znepi9.png"
        speed={0.3}
        overlayOpacity={0.45}
        minHeight="60vh"
      >
        <div className="h-[60vh] flex items-center">
          <div className="container mx-auto px-6 lg:px-12">
            <ParallaxElement speed={0.15} direction="up" className="max-w-xl">
              <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
                Il tocco finale per i tuoi ambienti
              </h2>
              <p className="text-lg text-white/80 font-light">
                Le tende non sono semplici accessori: sono elementi che definiscono l'atmosfera, 
                filtrano la luce e raccontano il tuo stile. Affidati a chi le crea con passione da oltre 60 anni.
              </p>
            </ParallaxElement>
          </div>
        </div>
      </ParallaxSection>

      {/* CTA Section */}
      <CTASection
        title="Trasforma i tuoi interni"
        description="Prenota una consulenza gratuita e scopri come le nostre tende artigianali possono valorizzare la tua casa."
        variant="light"
      />
    </Layout>
  );
};

export default TendeInternoPage;
